import { AxiosRequestConfig } from 'axios';

import { ISillyLogger } from './logger';
import { IRequest, Request } from './request';
import { IRequestOptions } from './request-options';
import { IEnableStatusChecksArg, IStatusArgs, IStatusService, StatusService } from './status';

let explicitlySetDefaultClient = false;
let defaultClient: IGatewayApiClient = null;

export function setDefaultClient(client: IGatewayApiClient) {
  explicitlySetDefaultClient = client !== null;
  defaultClient = client;
}

export function getDefaultClient(): IGatewayApiClient {
  return defaultClient;
}

export interface IGatewayApiClient {
  bearerOpts: AxiosRequestConfig;
  basicOpts: AxiosRequestConfig;
  noAuthOpts: AxiosRequestConfig;
  req: IRequest;
  hasBearer: boolean;
  fallbackOpts: AxiosRequestConfig;
  token: string;
  clientId: string;
  clientSecret: string;
  statusChecksEnabled: boolean;

  isDown(name: string): Promise<boolean>;

  getRequestConfig(options?: IRequestOptions): AxiosRequestConfig;
}

export interface IGatewayApiClientParameters {
  baseURL: string;
  cacheURL?: string;
  privateToken?: string;
  clientId?: string;
  clientSecret?: string;
  request?: IRequest;
  nodeProcess?: NodeJS.Process;
  status?: IStatusArgs & IEnableStatusChecksArg;
  logger?: ISillyLogger;
  profileNetworkRequests?: boolean;
}

export class GatewayApiClient implements IGatewayApiClient {
  public bearerOpts: AxiosRequestConfig;
  public basicOpts: AxiosRequestConfig;
  public noAuthOpts: AxiosRequestConfig;
  public req: IRequest;
  private statusService: IStatusService;
  private privateToken: string;
  private cacheURL: string;

  public get token(): string {
    return this.privateToken;
  }

  public set token(value: string) {
    if (value && !/^Bearer |^Basic /.test(value)) {
      throw new Error('Incomplete token. Token must have \'Bearer \' or \'Basic \'');
    }
    this.privateToken = value;
    this.bearerOpts.headers.Authorization = this.privateToken;
  }

  private _clientId: string;
  public get clientId(): string {
    return this._clientId;
  }

  public set clientId(value: string) {
    this._clientId = value;
    this.basicOpts.auth.username = this._clientId;
  }

  private _clientSecret: string;
  public get clientSecret(): string {
    return this._clientSecret;
  }

  public set clientSecret(value: string) {
    this._clientSecret = value;
    this.basicOpts.auth.password = this._clientSecret;
  }

  private readonly _statusChecksEnabled: boolean;
  public get statusChecksEnabled() {
    return this._statusChecksEnabled;
  }

  get hasBearer(): boolean {
    return !!this.privateToken;
  }

  get fallbackOpts(): AxiosRequestConfig {
    return this.hasBearer ? this.bearerOpts : this.basicOpts;
  }

  constructor(params: IGatewayApiClientParameters);
  constructor(
    baseURL: string,
    privateToken?: string,
    clientId?: string,
    clientSecret?: string,
    request?: IRequest,
    nodeProcess?: NodeJS.Process,
    statusOpts?: IStatusArgs & IEnableStatusChecksArg,
  );
  constructor(
    params: IGatewayApiClientParameters | string,
    privateToken?: string,
    clientId?: string,
    clientSecret?: string,
    request?: IRequest,
    nodeProcess?: NodeJS.Process,
    statusOpts?: IStatusArgs & IEnableStatusChecksArg,
    logger?: ISillyLogger,
    profileNetworkRequests?: boolean,
  ) {
    const baseURL = typeof params === 'object' ? params.baseURL : params;
    if (typeof params === 'object') {
      privateToken = params.privateToken;
      clientId = params.clientId;
      clientSecret = params.clientSecret;
      request = params.request;
      nodeProcess = params.nodeProcess;
      statusOpts = params.status;
      logger = params.logger;
      profileNetworkRequests = params.profileNetworkRequests;
      this.cacheURL = params.cacheURL;
    }
    if (nodeProcess === undefined) {
      nodeProcess = process;
    }

    this.privateToken = privateToken;
    if (!explicitlySetDefaultClient) {
      defaultClient = this;
    }
    this.req = request || new Request(
      undefined,
      logger,
      profileNetworkRequests,
    );

    if (this.isNode(nodeProcess)) {
      this.req.setUserAgentHeader(process.env.NEW_RELIC_APP_NAME || clientId);
    }

    const baseOpts = {
      baseURL,
    };

    this.noAuthOpts = { ...baseOpts };

    this.bearerOpts = {
      ...baseOpts,
      ...{
        headers: {
          Authorization: null,
        },
      },
    };

    this.basicOpts = {
      ...baseOpts,
      ...{
        auth: {
          password: clientSecret,
          username: clientId,
        },
      },
    };

    this.token = privateToken;
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    // Enable or disable by default, but still set up the status client for per method opt in
    this._statusChecksEnabled = statusOpts?.enableStatusChecks || false;
    this.statusService = new StatusService(statusOpts);
  }

  public getRequestConfig(options?: IRequestOptions): AxiosRequestConfig {
    const returnValue: AxiosRequestConfig = {
      ...(this.token && !options?.useBasicAuth ? this.bearerOpts : this.basicOpts),
      cancelToken: options?.cancelSource?.token,
    };
    if (options?.useGatewayCache) {
      if (!this.cacheURL) {
        throw new Error('cacheURL must be provided in GatewayApiClient constructor before useGatewayCache can be used!');
      }
      returnValue.baseURL = this.cacheURL;
    }
    return returnValue;
  }

  private isNode(process): boolean {
    return typeof process?.versions?.node !== 'undefined';
  }

  public isDown(name: string): Promise<boolean> {
    return this.statusService.isDown(name);
  }
}
